import AppContext from "PFApp/app_context";
import { useGrowl } from "PFApp/use_growl";
import { Button } from "PFComponents/button";
import Checkbox from "PFComponents/checkbox/checkbox";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { Icon } from "PFComponents/icon";
import InputFieldSet from "PFComponents/text/input_field_set";
import { getProfileName } from "PFCore/helpers/profile";
import { profileHash } from "PFCore/helpers/profile_hash";
import {
  useShortlistAssign,
  useShortlistDecision,
  useShortlistReset
} from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import CloseIcon from "PFIcons/close.svg";
import OkIcon from "PFIcons/ok.svg";
import colors from "PFTheme/tokens/colors";
import PropTypes from "prop-types";
import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import ButtonLabel from "./button_label";
import css from "./shortlist_buttons.less";

export const InviteConfirmModal = ({ setDisplayConfirm, handleSubmit }) => {
  const cancel = useCallback(() => {
    setReasons([]);
    setOtherReason("");
    setDisplayConfirm(false);
  }, []);
  const { t } = useTranslation("activities");

  const shortlistReasons = [
    { id: "availability", text: t("parts.availability") },
    { id: "skill", text: t("parts.skill") },
    { id: "location", text: t("parts.location") },
    { id: "cost", text: t("parts.cost") },
    { id: "experience", text: t("parts.experience") },
    { id: "other", text: t("parts.other") }
  ];

  const [reasons, setReasons] = useState([]);
  const [otherReason, setOtherReason] = useState("");
  const [otherReasonError, setOtherReasonError] = useState(undefined);

  const submit = () => {
    if (reasons.find((reason) => reason === "other") && otherReason === "") {
      setOtherReasonError(t("parts.reasonForOther"));
      return;
    }

    const reasonValues = reasons.reduce(
      (acc, id) => {
        const reasonText = shortlistReasons.find((reason) => reason.id === id).text;
        if (id === "other") {
          acc.other = otherReason;
        } else {
          acc.list.push(reasonText);
        }
        return acc;
      },
      { list: [] }
    );

    handleSubmit(reasonValues);
  };

  const handleOtherReason = (val) => {
    setOtherReason(val);
    setOtherReasonError(undefined);
  };

  return (
    <ConfirmModal
      showOkButton
      labelOK="Submit"
      handleOK={submit}
      handleClose={cancel}
      executeCloseOnOk={false}
    >
      <h3>{t("parts.decisionBasedOn")}</h3>
      {shortlistReasons.map((item) => (
        <label className={css.reason} key={item.id} htmlFor={item.id}>
          <Checkbox
            handleChange={(checked) =>
              setReasons(checked ? [...reasons, item.id] : reasons.filter((value) => value !== item.id))
            }
            checked={!!reasons.find((value) => value === item.id)}
            label={item.text}
            id={item.id}
          />
        </label>
      ))}
      {reasons.find((reason) => reason === "other") && (
        <InputFieldSet
          maxLength={500}
          value={otherReason}
          handleChange={handleOtherReason}
          error={otherReasonError}
        />
      )}
      <div className={css.visibilityCopy}>{t("parts.reasonVisibility")}</div>
    </ConfirmModal>
  );
};

InviteConfirmModal.propTypes = {
  setDisplayConfirm: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired
};

// pending <-> declined
//         <-> accepted -> assigned ==[by the shortlisted]=> rejected
//                                  ==[by the shortlisted]=> filled

// use when "pending"
export const AcceptDeclineButton = ({ action, shortlist, isTaskOwner, onDone, qaPrefix, text, style }) => {
  const [pending, setPending] = useState(false);
  const [displayConfirm, setDisplayConfirm] = useState(false);
  const {
    store: { themeVars }
  } = useContext(AppContext);
  const growl = useGrowl();
  const { t } = useTranslation("activities");
  const { mutate: makeShortlistDecision } = useShortlistDecision();
  const { mutate: resetShortlist } = useShortlistReset();

  const handleDone = (resp) => {
    setPending(false);
    onDone && onDone(resp);
  };

  const handleError = () => {
    growl({
      kind: "error",
      message: t("parts.problemUpdatingShortlist")
    });
  };

  const handleClick = () => {
    setDisplayConfirm(true);
  };

  const performDecisionRequest = ({ params, onError }) => {
    makeShortlistDecision(params, {
      onSuccess: handleDone,
      onError
    });
  };

  const handleSubmit = (reasonValues) => {
    setDisplayConfirm(false);
    setPending(true);

    const params = { shortlistId: shortlist.id, action, reasons: reasonValues };

    performDecisionRequest({
      params,
      onError: (err) => {
        if (err?.status === 422) {
          resetShortlist(shortlist.id, {
            onSuccess: () => {
              performDecisionRequest({ params });
            },
            onError: () => handleError()
          });
        } else {
          handleError();
        }
      }
    });
  };

  const _style = { minWidth: 100, ...style };

  return (
    <>
      <Button kind="primary" style={_style} onClick={handleClick} qaId={`${qaPrefix}-${action}`}>
        {pending && "..."}
        {!pending &&
          (action === "accept" ? (
            <>
              <OkIcon
                width={16}
                height={16}
                style={{ marginRight: 5, fill: themeVars["--Palette-action"] }}
              />
              {` ${text || (isTaskOwner ? t("accept") : t("parts.approve"))}`}
            </>
          ) : (
            <>
              <CloseIcon
                width={16}
                height={16}
                style={{ marginRight: 5, fill: themeVars["--Palette-error"] }}
              />
              {` ${t("decline")}`}
            </>
          ))}
      </Button>
      {displayConfirm && (
        <InviteConfirmModal setDisplayConfirm={setDisplayConfirm} handleSubmit={handleSubmit} />
      )}
    </>
  );
};

const SuccessLabel = ({ qaPrefix, text, style }) => {
  const {
    store: { themeVars }
  } = useContext(AppContext);
  return (
    <ButtonLabel style={style} qaId={qaPrefix}>
      <span className={css.approvedIcon}>
        <OkIcon width={16} height={16} style={{ marginRight: 5, fill: themeVars["--Palette-action"] }} />
      </span>
      {text}
    </ButtonLabel>
  );
};

// use when "accepted"
export const AcceptedLabel = ({ isTaskOwner, qaPrefix, text, style }) => {
  const { t } = useTranslation("activities");

  return (
    <SuccessLabel
      style={style}
      text={text || (isTaskOwner ? t("accepted") : t("parts.approved"))}
      qaId={`${qaPrefix}-accepted`}
    />
  );
};

export const InvitedLabel = ({ qaPrefix, style }) => {
  const { t } = useTranslation("activities");

  return <SuccessLabel style={style} text={t("invited")} qaId={`${qaPrefix}-invited`} />;
};

// use when "accepted"
export const AssignButton = ({ shortlist, style, onDone, text, qaPrefix, isUnbiased }) => {
  const hasBeenInvited = shortlist.state === "assigned";
  const growl = useGrowl();
  const { t } = useTranslation("activities");
  const { mutate: assignShortlist } = useShortlistAssign();

  const [pending, setPending] = useState(false);
  const handleDone = (resp) => {
    setPending(false);
    onDone && onDone(resp);
  };

  const handleClick = () => {
    setPending(true);
    assignShortlist(shortlist.id, {
      onSuccess: (resp) => {
        handleDone(resp);
        const name = isUnbiased
          ? t("parts.candidateHash", { profileHash: profileHash(shortlist.profile.id) })
          : getProfileName(shortlist.profile);
        growl({
          kind: "success",
          message: t("parts.roleAssignedNotification", { name })
        });
      },
      onError: handleDone
    });
  };

  const _style = { minWidth: 75, ...style };
  return hasBeenInvited ? (
    <InvitedLabel qaPrefix={qaPrefix} />
  ) : (
    <Button
      text={pending ? "..." : `${text || (hasBeenInvited ? t("invited") : t("invite"))}`}
      kind="buttonGroup"
      disabled={hasBeenInvited}
      style={_style}
      onClick={handleClick}
      qaId={`${qaPrefix}-assign`}
    />
  );
};

// use when "assigned"
export const AssignedLabel = ({ text, qaPrefix }) => {
  const { t } = useTranslation("activities");

  return <ButtonLabel qaId={`${qaPrefix}-assigned`}>{text || t("parts.invitedToRole")}</ButtonLabel>;
};

// use when "declined"
// use when "filled"
// use when "rejected"
export const FinalLabel = ({ style, qaPrefix, kind }) => {
  const { t } = useTranslation("activities");

  const copy =
    {
      declined: t("declined"),
      filled: t("parts.filled"),
      rejected: t("rejected"),
      booked: t("booked"),
      confirmed: t("confirmed")
    }[kind] || "";
  return (
    <ButtonLabel style={style} qaId={`${qaPrefix}-${kind}`}>
      {["filled", "booked", "confirmed"].includes(kind) ? (
        <Icon name="user-filled" size="sm" />
      ) : (
        <Icon name="cross" size="sm" fill={colors.paletteRed0} />
      )}
      {copy}
    </ButtonLabel>
  );
};

FinalLabel.defaultProps = {
  qaPrefix: "Label"
};

FinalLabel.propTypes = {
  qaPrefix: PropTypes.string,
  style: PropTypes.object,
  kind: PropTypes.oneOf(["declined", "filled", "rejected", "booked"]).isRequired
};

const defaultProps = {
  qaPrefix: "Button"
};

AcceptDeclineButton.defaultProps =
  AcceptedLabel.defaultProps =
  SuccessLabel.defaultProps =
  InvitedLabel.defaultProps =
  AssignButton.defaultProps =
  AssignedLabel.defaultProps =
    defaultProps;

const propTypes = {
  action: PropTypes.string,
  text: PropTypes.string,
  shortlist: PropTypes.shape({
    id: PropTypes.number,
    state: PropTypes.oneOf(["pending", "accepted", "declined", "assigned", "filled", "rejected"]),
    profile: PropTypes.object,
    activity_id: PropTypes.number
  }).isRequired,
  isTaskOwner: PropTypes.bool.isRequired,
  isUnbiased: PropTypes.bool,
  onDone: PropTypes.func,

  style: PropTypes.object,
  qaPrefix: PropTypes.string
};

AcceptDeclineButton.propTypes =
  SuccessLabel.propTypes =
  InvitedLabel.propTypes =
  AcceptedLabel.propTypes =
  AssignButton.propTypes =
  AssignedLabel.propTypes =
    propTypes;
